import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/api/client';
import { REPAIR_STATUSES } from '@/utils/helpers';
import { Search, Truck } from 'lucide-react';

export const RepairForm = ({ isOpen, onClose, repair = null, onSaved }) => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [creatingRMA, setCreatingRMA] = useState(false);
  const [currentRepair, setCurrentRepair] = useState(repair);

  const [formData, setFormData] = useState({
    customer_id: '',
    device: '',
    product_id: '',
    serial_number: '',
    issue: '',
    status: 'Pending',
    tech_notes: '',
    cost: 0,
    paid: false,
    linked_rma_id: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadRefData();
      setCurrentRepair(repair);
      if (repair) {
        setFormData({
          customer_id: repair.customer_id || '',
          device: repair.device || '',
          product_id: repair.product_id || '',
          serial_number: repair.serial_number || '',
          issue: repair.issue || '',
          status: repair.status || 'Pending',
          tech_notes: repair.tech_notes || '',
          cost: repair.cost || 0,
          paid: repair.paid || false,
          linked_rma_id: repair.linked_rma_id || ''
        });
      } else {
        setFormData({
          customer_id: '', device: '', product_id: '', serial_number: '', issue: '', status: 'Pending',
          tech_notes: '', cost: 0, paid: false, linked_rma_id: ''
        });
      }
    }
  }, [isOpen, repair]);

  const loadRefData = async () => {
    try {
      const [c, p] = await Promise.all([api.getCustomers(), api.getProducts()]);
      setCustomers(c.data);
      setProducts(p.data);
    } catch (e) { console.error(e); }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone) {
      alert('Name and phone required');
      return;
    }
    try {
      const res = await api.createCustomer(newCustomer);
      setCustomers([...customers, res.data]);
      setFormData({ ...formData, customer_id: res.data.id });
      setShowNewCustomer(false);
      setNewCustomer({ name: '', phone: '', email: '' });
    } catch (e) { alert('Failed'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.device || !formData.issue) {
      alert('Customer, device, and issue are required');
      return;
    }
    setLoading(true);
    try {
      let saved;
      if (currentRepair) {
        const r = await api.updateRepair(currentRepair.id, formData);
        saved = r.data;
      } else {
        const r = await api.createRepair(formData);
        saved = r.data;
      }
      setCurrentRepair(saved);
      onSaved && onSaved();
      // Don't close if new so user can immediately create supplier RMA
      if (currentRepair) onClose();
    } catch (error) {
      alert('Failed to save repair');
    } finally { setLoading(false); }
  };

  const handleCreateSupplierRMA = async () => {
    if (!currentRepair) { alert('Save the repair first'); return; }
    if (!formData.product_id || !formData.serial_number) {
      alert('Please link a product and serial number before creating a Supplier RMA.');
      return;
    }
    if (currentRepair.linked_rma_id) {
      alert('This repair already has a supplier RMA linked.');
      return;
    }
    const notes = window.prompt('Optional notes for supplier RMA:', '') || '';
    setCreatingRMA(true);
    try {
      const res = await api.repairCreateSupplierRMA(currentRepair.id, { notes });
      alert(`Supplier RMA ${res.data.job_id} created and linked.`);
      setCurrentRepair({ ...currentRepair, linked_rma_id: res.data.id, status: 'With Supplier' });
      setFormData(prev => ({ ...prev, linked_rma_id: res.data.id, status: 'With Supplier' }));
      onSaved && onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to create supplier RMA');
    } finally { setCreatingRMA(false); }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone.includes(customerSearch)
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={repair ? 'Edit Repair' : 'Create New Repair Ticket'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Customer */}
        <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
          <div className="flex justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Customer *</label>
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowNewCustomer(!showNewCustomer)}>
              {showNewCustomer ? 'Select Existing' : '+ New Customer'}
            </Button>
          </div>
          {showNewCustomer ? (
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Name *" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} data-testid="repair-new-customer-name" />
              <Input placeholder="Phone *" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} data-testid="repair-new-customer-phone" />
              <div className="flex gap-1">
                <Input placeholder="Email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} />
                <Button type="button" size="sm" onClick={handleCreateCustomer}>Add</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                <Input placeholder="Search customers..." value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="pl-9" />
              </div>
              <Select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} data-testid="repair-customer-select">
                <option value="">-- Select Customer --</option>
                {filteredCustomers.map(c => <option key={c.id} value={c.id}>{`${c.name} (${c.phone})`}</option>)}
              </Select>
            </>
          )}
        </div>

        <Input label="Device *" placeholder="e.g. Dell XPS 15 Laptop" value={formData.device} onChange={(e) => setFormData({ ...formData, device: e.target.value })} data-testid="repair-device" />

        {/* Linked Product & Serial — required for Supplier RMA workflow */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
          <label className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 block">
            Linked Product &amp; Serial (optional — required to create a Supplier RMA)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Select value={formData.product_id}
              onChange={(e) => setFormData({ ...formData, product_id: e.target.value, serial_number: '' })}
              data-testid="repair-product-select">
              <option value="">— None —</option>
              {products.map(p => <option key={p.id} value={p.id}>{`${p.name}${p.brand ? ` · ${p.brand}` : ''}`}</option>)}
            </Select>
            <Input placeholder="Serial (e.g. CMK004)" value={formData.serial_number}
              onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              data-testid="repair-serial-number" />
          </div>
        </div>

        <Textarea label="Issue Reported *" value={formData.issue} onChange={(e) => setFormData({ ...formData, issue: e.target.value })} rows={3} data-testid="repair-issue" />
        <Textarea label="Tech Notes" value={formData.tech_notes} onChange={(e) => setFormData({ ...formData, tech_notes: e.target.value })} rows={2} data-testid="repair-tech-notes" />

        <div className="grid grid-cols-3 gap-4">
          <Select label="Status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} data-testid="repair-status">
            {REPAIR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Input label="Cost" type="number" step="0.01" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })} data-testid="repair-cost" />
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.paid} onChange={(e) => setFormData({ ...formData, paid: e.target.checked })} className="w-4 h-4" data-testid="repair-paid" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Paid</span>
            </label>
          </div>
        </div>

        {currentRepair && (
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
                  <Truck size={14} /> Supplier RMA
                </div>
                {currentRepair.linked_rma_id ? (
                  <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                    Linked to supplier RMA. Manage it in the RMAs page.
                  </p>
                ) : (
                  <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">
                    Send this faulty item back to the supplier — creates a linked RMA record.
                  </p>
                )}
              </div>
              <Button type="button" variant="secondary" size="sm"
                disabled={!!currentRepair.linked_rma_id || creatingRMA || !formData.product_id || !formData.serial_number}
                onClick={handleCreateSupplierRMA} data-testid="repair-create-supplier-rma">
                {creatingRMA ? 'Creating…' : (currentRepair.linked_rma_id ? 'Already Linked' : 'Create Supplier RMA')}
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={loading} data-testid="save-repair">
            {loading ? 'Saving...' : (currentRepair ? 'Update Repair' : 'Create Repair')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
